/**
 * Phase 6 Plan 02 — PhotoStrip (HIST-5.7, D-135).
 * Phase 6 Plan 05 — Gap-closure CR-03/CR-04 (placeholder + broken-contract).
 * Phase 13 Plan 02 — Migrated to <PhotoTile state={...}> via mapPmEntryToTileState.
 *
 * Horizontal strip of photo tiles inside InspectionRow expanded state.
 * Container responsibilities only:
 *   - Reads useInspectionPhotos (TanStack Query — SSE-invalidated on photo_uploaded).
 *   - Maps each PmPhotoEntry to a PmPhotoTileState via mapPmEntryToTileState() (D-03).
 *   - Constructs an Intl.DateTimeFormat with numberingSystem: 'latn' (Blocker #1 +
 *     V-CP4 Latin-digit lock; D-11 verbatim from CONTEXT.md). Container is the
 *     formatting tier per Warning #8 — NOT the mapping function (which returns
 *     pure type-narrowed payload) and NOT the tile (which renders).
 *   - Threads formattedDate down to <PhotoTile> for {date} interpolation.
 *   - Renders PhotoLightbox modal when a landed tile is clicked.
 *
 * Rendering moved entirely into PhotoTile.tsx — the inline if/then/else over
 * 3 sources is gone (V-M2 surface unification + ~50 LOC delete from the
 * container). PhotoTile owns the visual treatment per D-05/D-06/D-08/D-19.
 *
 * Photos do NOT mirror in RTL — the tile uses logical-property utilities
 * (top-end / bottom-end pill positions) so chrome direction flips but <img>
 * content stays canonical. CLAUDE.md hard constraint preserved.
 *
 * src for landed thumbnails routes through the PM-auth-gated thumbnail
 * endpoint /api/admin/photos/{id}/thumb (server/app/photos/binary_router.py)
 * — NOT the raw storage_path which is a server-internal filesystem path
 * (operator UAT 2026-05-06 found this rendered as HTML through Caddy's SPA
 * fallback, breaking every thumbnail).
 *
 * @SpecRef PHOTOSTRIP-6.1
 * @SpecRef PHOTOSTRIP-6.2
 */
import { useState } from "react";
import { useIntl } from "react-intl";
import { useInspectionPhotos } from "../useInspectionPhotos";
import { PhotoLightbox } from "./PhotoLightbox";
import { PhotoTile } from "./PhotoTile";
import { mapPmEntryToTileState } from "./mapPmEntryToTileState";

interface PhotoStripProps {
  inspectionId: number;
  expanded: boolean;
}

export function PhotoStrip({ inspectionId, expanded }: PhotoStripProps) {
  const intl = useIntl();
  const query = useInspectionPhotos(inspectionId, expanded);
  const photos = query.data?.photos ?? [];
  // 2026-05-06 — clicked thumbnail opens PhotoLightbox at full resolution.
  // 80×80 thumbs are too small for the operator to triage condition.
  const [openPhotoId, setOpenPhotoId] = useState<number | null>(null);
  if (photos.length === 0) return null;

  // Phase 13 Blocker #1 + V-CP4 — container-tier date formatter. Constructed
  // ONCE per render (not per photo); the formatter object is locale-scoped via
  // intl.locale (resolved from the IntlProvider context). numberingSystem: 'latn'
  // is the Latin-digit lock per CLAUDE.md hard constraint (Phase 8 V-CP4) — the
  // ESLint rule at web/eslint.config.js:78 enforces this option on every
  // Intl.DateTimeFormat / Intl.NumberFormat construction. Date.prototype.toLocaleString
  // is NOT used because it does not accept the numberingSystem option (V-CP4
  // would silently break in Arabic locale).
  const dateFormatter = new Intl.DateTimeFormat(intl.locale, {
    numberingSystem: 'latn',
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <>
      <section
        className="mbs-3 flex flex-row gap-2 overflow-x-auto"
        data-testid={`photo-strip-${inspectionId}`}
      >
        {photos.map((p) => {
          const tileState = mapPmEntryToTileState(p);
          // D-11 — empty-string fall-through is the documented null state for
          // a not-yet-uploaded photo (uploading kind on PM side has no
          // uploaded_at yet). PhotoTile's intl.formatMessage(..., { date }) is
          // safe to receive '' — it just renders the message template's tail
          // without a timestamp.
          const formattedDate = p.uploaded_at
            ? dateFormatter.format(new Date(p.uploaded_at))
            : '';
          return (
            <PhotoTile
              key={p.id}
              state={tileState}
              thumbUrlBuilder={(id) => `/api/admin/photos/${id}/thumb`}
              onLandedClick={setOpenPhotoId}
              formattedDate={formattedDate}
            />
          );
        })}
      </section>
      {openPhotoId !== null && (
        <PhotoLightbox
          photoId={openPhotoId}
          onClose={() => setOpenPhotoId(null)}
        />
      )}
    </>
  );
}
